import { Game, Round } from '@/redux/reducers/gameReducer'
import { LottoGameCardProps } from '@/ui-components/components/LottoGameCard'
import { useTranslation } from 'react-i18next'
import { getLottoNameAndCountryFromLottoGame } from '@/utils/lotto-type-v2'
import {
  convertToDateTime,
  DATE_TIME_FORMAT_HUMAN_SHORT,
} from '@/utils/date-time-utils'
import { isYeekeeType, isEncryptLotto } from '@/utils/lotto-type-v2'
import dayjs from 'dayjs'
import { trans } from '@/i18n/utils'

const useLottoCardForLottoRoundPage = (): ((
  game: Game,
  rounds?: Round[],
) => LottoGameCardProps[]) => {
  const { i18n, t } = useTranslation()

  // Helper to filter and sort rounds like useRoundPage
  const filterAndSortRounds = (rounds: Round[]) => {
    const now = dayjs()
    const closedRound = rounds
      .filter((round) => dayjs(round.close_at).isBefore(now))
      .sort((a, b) => dayjs(b.close_at).diff(dayjs(a.close_at)))
    const openRound = rounds
      .filter((round) => dayjs(round.close_at).isAfter(now))
      .sort((a, b) => dayjs(a.close_at).diff(dayjs(b.close_at)))
    return [...openRound, ...closedRound]
  }

  const getRoundGameTitle = (game: Game, i?: number) =>
    t('lottoPage.lottoRound.round_name', {
      name: trans(i18n, game.translations, 'name'),
      round: i,
    })

  return (game: Game, rounds?: Round[]): LottoGameCardProps[] => {
    const { name, country } = getLottoNameAndCountryFromLottoGame(
      game.type,
      game.flag,
    )
    const gameRounds = rounds || (Array.isArray(game.rounds) ? game.rounds : [])
    const filteredRounds = filterAndSortRounds(gameRounds)
    return filteredRounds.map((round) => {
      const isOpen =
        game.is_enable === 'yes' &&
        round.is_enable === 'yes' &&
        dayjs(round.close_at).isAfter(dayjs())
      const timeRemaining =
        round.is_enable === 'yes' ? new Date(round.close_at) : undefined
      const closeAtTxt = convertToDateTime(
        round.close_at,
        DATE_TIME_FORMAT_HUMAN_SHORT,
        i18n.language,
      )
      const closedTime = t('lottoPage.status.close_at', { date: closeAtTxt })

      const resp: LottoGameCardProps = {
        id: round.id.toString(),
        country,
        flagBannerName: name,
        title: getRoundGameTitle(game, round.round_number),
        closedTime,
        isOpen: !!isOpen,
        timeRemaining,
        gameLink: `/lotto/${game.code}/${game.id}`,
        activeRoundLink: `/lotto/${game.code}/${game.id}/${round.id}`,
      }

      if (
        (isYeekeeType(game.type) || isEncryptLotto(game.type)) &&
        resp.activeRoundLink
      ) {
        // If it's a Yeekee or Encrypt Lotto, we set the active round link to the game link
        // User will force to bet page regardless of round state
        resp.gameLink = resp.activeRoundLink
      }

      return resp
    })
  }
}

export default useLottoCardForLottoRoundPage
